// blitzpaypagecomponents/BlitzPaySummaryCards.jsx - Improved Desktop Layout
import React from "react";
import { 
  Grid, 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  Warning as WarningIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from '@mui/material/styles';
import TeamSpendingCard from "./TeamSpendingCard";
import CompactMetricCard from "./CompactMetricCard"
import { formatCurrency } from "../../../Utils/formatNumbers";

const BlitzPaySummaryCards = ({
  spendingInfo,
  spendingLoading,
  pendingItems,
  amountInEscrow,
  creatorsPaid,
  currentBalance,
  onShowSpendingDetails,
  onShowPendingItems,
  onShowBalanceLedger,
  hasBlitzPayAccount = false,
  companyInfo = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const hasMSA = companyInfo?.msa_initiated?.initiated === true;
  const hasStarterLine = companyInfo?.starter_line_initiated?.initiated === true;
  const hasAnyTrack = hasMSA || hasStarterLine;

  // Featured Balance Card - Hero treatment for balance
  const FeaturedBalanceCard = () => (
    <Card
      onClick={onShowBalanceLedger}
      sx={{
        height: '100%',
        minHeight: { md: 85, lg: 95 },
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'info.main',
        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.light, 0.04)} 100%)`,
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.2)}`,
          borderColor: 'info.dark',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '220px',
          height: '220px',
          background: `radial-gradient(circle, ${alpha(theme.palette.info.main, 0.08)} 0%, transparent 70%)`,
          transform: 'translate(30%, -30%)',
        }
      }}
    >
      <CardContent sx={{ p: 3.5, position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={2.5} sx={{ height: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box
              sx={{
                width: { md: 56, lg: 60 },
                height: { md: 56, lg: 60 },
                borderRadius: 3,
                bgcolor: alpha(theme.palette.info.main, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 6px 20px ${alpha(theme.palette.info.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <WalletIcon sx={{ fontSize: { md: 28, lg: 32 }, color: 'info.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  display: 'block',
                  mb: 0.75
                }}
              >
                Current Balance
              </Typography>
              <Typography 
                variant="h3" 
                fontWeight={800}
                color="info.main"
                sx={{ 
                  fontSize: { md: '2.25rem', lg: '2.75rem' },
                  lineHeight: 0.95,
                  letterSpacing: '-0.03em'
                }}
              >
                {formatCurrency(currentBalance || 0)}
              </Typography>
            </Box>
          </Stack>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.875rem', fontWeight: 500, mt: 'auto' }}
          >
            Available for transfers and payments
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  // Track Status Card for Desktop
  const TrackStatusCard = () => (
    <Card
      sx={{
        height: '100%',
        minHeight: { md: 85, lg: 95 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: hasAnyTrack ? 'success.main' : 'divider',
        bgcolor: hasAnyTrack ? alpha(theme.palette.success.main, 0.04) : 'background.paper',
        transition: 'all 0.3s',
        position: 'relative',
        overflow: 'hidden',
        '&::before': hasAnyTrack ? {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.06)} 0%, transparent 70%)`,
          transform: 'translate(30%, -30%)',
        } : {},
      }}
    >
      <CardContent sx={{ p: 2.5, pb: "20px !important", height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
          </Box>
        </Box>
        
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 0.75,
          }}
        >
          Your Track
        </Typography>
        
        <Box sx={{ mt: 'auto' }}>
          {hasAnyTrack ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
              {hasMSA && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label="MSA"
                  size="small"
                  color="success"
                  sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, px: 0.5 }}
                />
              )}
              {hasStarterLine && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label="Starter"
                  size="small"
                  color="success"
                  variant={hasMSA ? "outlined" : "filled"}
                  sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, px: 0.5 }}
                />
              )}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
              No track active
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // MOBILE LAYOUT - Keep existing mobile design
  if (isMobile) {
    return (
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {/* Featured Card - TeamSpending if BlitzPay, Balance otherwise */}
          <Grid item xs={12}>
            {hasBlitzPayAccount ? (
              <TeamSpendingCard
                spendingInfo={spendingInfo}
                loading={spendingLoading}
                onClick={onShowSpendingDetails}
              />
            ) : (
              <FeaturedBalanceCard />
            )}
          </Grid>

          {/* Compact Metrics - 2x2 grid */}
          <Grid item xs={6}>
            <Card
              onClick={onShowPendingItems}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: pendingItems > 0 ? 'warning.main' : 'divider',
                bgcolor: pendingItems > 0 ? alpha(theme.palette.warning.main, 0.04) : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:active': { transform: 'scale(0.98)' }
              }}
            >
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 'auto' }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                    >
                      Pending
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight={700}
                      sx={{ fontSize: '1.25rem', lineHeight: 1.1 }}
                    >
                      {pendingItems}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}
                    >
                      Need action
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Show balance as compact metric if BlitzPay account exists */}
          {hasBlitzPayAccount && (
            <Grid item xs={6}>
              <Card
                onClick={onShowBalanceLedger}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:active': { transform: 'scale(0.98)' }
                }}
              >
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 'auto' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <WalletIcon sx={{ fontSize: 20, color: 'info.main' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                      >
                        Your Personal Balance
                      </Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight={700}
                        sx={{ fontSize: '1.25rem', lineHeight: 1.1 }}
                      >
                        {formatCurrency(currentBalance || 0)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}
                      >
                        Current
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Track Status */}
          {hasBlitzPayAccount && (
            <Grid item xs={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: hasAnyTrack ? 'success.main' : 'divider',
                  bgcolor: hasAnyTrack ? alpha(theme.palette.success.main, 0.04) : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 1 }}
                  >
                    Your Track
                  </Typography>
                  <Box sx={{ mt: 'auto' }}>
                    {hasAnyTrack ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                        {hasMSA && (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="MSA"
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        )}
                        {hasStarterLine && (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="Starter"
                            size="small"
                            color="success"
                            variant={hasMSA ? "outlined" : "filled"}
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        No track active
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Creators Paid */}
          <Grid item xs={6}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 'auto' }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                    >
                      Creators
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight={700}
                      sx={{ fontSize: '1.25rem', lineHeight: 1.1 }}
                    >
                      {creatorsPaid}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}
                    >
                      Paid out
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // DESKTOP LAYOUT - Redesigned with Better Balance
  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {/* Featured Card - 4 columns */}
        <Grid item xs={12} md={6} lg={4}>
          {hasBlitzPayAccount ? (
            <TeamSpendingCard
              spendingInfo={spendingInfo}
              loading={spendingLoading}
              onClick={onShowSpendingDetails}
            />
          ) : (
            <FeaturedBalanceCard />
          )}
        </Grid>

        {/* Metrics Grid - 8 columns */}
        <Grid item xs={12} md={6} lg={8}>
          <Grid container spacing={2.5} sx={{ height: '100%' }}>
            {/* Top Row - Balance and Pending (if BlitzPay) or just Pending and Creators */}
            {hasBlitzPayAccount ? (
              <>
                <Grid item xs={6}>
                  <CompactMetricCard
                    icon={<WalletIcon />}
                    title="Your Personal Balance"
                    value={formatCurrency(currentBalance || 0)}
                    subtitle="Available funds"
                    color="info"
                    onClick={onShowBalanceLedger}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CompactMetricCard
                    icon={<WarningIcon />}
                    title="Pending"
                    value={pendingItems}
                    subtitle="Require action"
                    color="warning"
                    onClick={onShowPendingItems}
                    alert={pendingItems > 0}
                  />
                </Grid>
                
                {/* Bottom Row - Track Status and Creators */}
                <Grid item xs={6}>
                  <TrackStatusCard />
                </Grid>
                <Grid item xs={6}>
                  <CompactMetricCard
                    icon={<PeopleIcon />}
                    title="Vendors/Creators"
                    value={creatorsPaid}
                    subtitle="Successfully paid"
                    color="success"
                  />
                </Grid>
              </>
            ) : (
              <>
                {/* No BlitzPay - Show Pending and Creators side by side, full height */}
                <Grid item xs={6}>
                  <CompactMetricCard
                    icon={<WarningIcon />}
                    title="Pending Items"
                    value={pendingItems}
                    subtitle="Require action"
                    color="warning"
                    onClick={onShowPendingItems}
                    alert={pendingItems > 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CompactMetricCard
                    icon={<PeopleIcon />}
                    title="Creators"
                    value={creatorsPaid}
                    subtitle="Successfully paid"
                    color="success"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BlitzPaySummaryCards;